import React, { FC, useCallback, useState } from 'react'

import { Box, Button, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { ContactUsButton } from '../../components/ContactUs'
import { ContactUsModal } from '../../components/ContactUs/ContactUsModal'
import { ModalLocation } from '../../components/ContactUs/types'
import { CustomButton } from '../../components/CustomButton'
import { clearError } from '../../components/ErrorBoundary'
import { LogoImg } from '../../components/Logo'
import { APP_PATHS } from '../../routes/paths'
import { useAppDispatch } from '../../store/hooks'
import { useUser } from '../../user'

import ErrorInfo from './ErrorInfo'
import { IErrorProps } from './types'

const Error: FC<IErrorProps> = ({ data }) => {
  const [hiddenInfo, setHiddenInfo] = useState(true)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { spacing } = useTheme()
  const { t } = useTranslation()

  const { isOnboadringCompleted, isAuthentificated } = useUser()

  const handleReturn = useCallback(() => {
    dispatch(clearError())

    let path = APP_PATHS.auth

    if (isAuthentificated) {
      path = isOnboadringCompleted ? APP_PATHS.dashboard : APP_PATHS.onboarding
    } else {
      path = APP_PATHS.auth
    }

    navigate(path, { replace: true })
  }, [])

  const handleHide = useCallback(() => setHiddenInfo((prevState) => !prevState), [setHiddenInfo])

  return (
    <>
      <Box
        p={{ xs: 4, md: 7 }}
        display="flex"
        flexDirection={'column'}
        minWidth={'100%'}
        overflow="auto"
      >
        <Box sx={{ cursor: 'pointer' }} onClick={handleReturn}>
          <LogoImg />
        </Box>

        <Box maxWidth={spacing(73.5)} width={'100%'} mx="auto" textAlign="center" pt={10}>
          {hiddenInfo && (
            <Box mb={5} mx={'auto'}>
              <ErrorIcon />
            </Box>
          )}

          <Typography variant="h2" mb={4}>
            {t('errorPage.title', 'Something went wrong')}
          </Typography>

          <Typography
            variant="body1"
            fontSize={spacing(2)}
            lineHeight={1.5}
            fontWeight={500}
            color={'text.secondary'}
            mb={5}
          >
            {t(
              'errorPage.message.intro',
              'We apologize for the inconvenience, but it seems that an error has occurred.',
            )}
            <br />
            {hiddenInfo ? (
              <>
                {t(
                  'errorPage.message.resolution',
                  'Our team has been notified and is working to resolve the issue.',
                )}
              </>
            ) : (
              <>
                {t(
                  'errorPage.message.contact',
                  'If the error still occurs, please contact the back office.',
                )}
              </>
            )}
          </Typography>

          <CustomButton type="button" onClick={handleReturn}>
            {isOnboadringCompleted
              ? t('errorPage.actions.backToDashboard', 'Back to dashboard')
              : t('errorPage.actions.backToLogin', 'Back to Login')}
          </CustomButton>

          <Box mt={4}>
            <Button
              disableRipple
              onClick={handleHide}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: spacing(2),
                lineHeight: 0.9,
                color: 'text.secondary',
                padding: 0,
                fontWeight: 500,
                borderBottom: (theme) => `1px solid ${theme.palette.text.secondary}`,
                textTransform: 'initial',
                minWidth: 'auto',
                borderRadius: 0,
                transition: 'none',
                '&:hover': {
                  background: 'transparent',
                },
              }}
            >
              {hiddenInfo
                ? t('errorPage.actions.details', 'Details')
                : t('errorPage.actions.hideDetails', 'Hide details')}
            </Button>
          </Box>

          {!hiddenInfo && <ErrorInfo data={data} />}
        </Box>
      </Box>
      <ContactUsButton position={ModalLocation.left} />
      <ContactUsModal />
    </>
  )
}

export default Error
